// Whether a number is on the blocked list right now, with the action to block it or change its entry.
// Shown beside a contact's numbers and on a call's detail page. Reads need blocklist:read; the action
// needs blocklist:manage. Nothing is shown to people who can't read the list.
import { useEffect, useState } from 'react';
import { Ban } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { type BlocklistClient, type BlocklistEntry, isAbortError } from '../../lib/api.ts';
import { BlockNumberButton } from './BlockNumberDialog.tsx';
import { BlocklistInlineStyles } from './InlineStyles.tsx';
import { type BlocklistAccess, BLOCK_LISTS, clipReason, listsText, readLookup } from './model.ts';

/** "Blocked for incoming calls and texts"; all three lists read as "calls and texts". */
export function blockedChipText(entry: Pick<BlocklistEntry, 'lists'>): string {
  return entry.lists.length === BLOCK_LISTS.length ? 'Blocked for calls and texts' : `Blocked for ${listsText(entry.lists)}`;
}

export function NumberStatus({ e164, client, access, noteSeed, label,showControl=true }: {
  showControl?:boolean;
  e164: string;
  client: BlocklistClient;
  access: BlocklistAccess;
  noteSeed?: string | undefined;
  /** Names the number for screen readers when several are listed together. */
  label?: string;
}) {
  const [state, setState] = useState<{ status: 'loading' | 'ready' | 'error'; entry: BlocklistEntry | null }>({ status: 'loading', entry: null });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!access.read) return;
    const controller = new AbortController();
    client.lookup(e164, controller.signal).then(
      body => {
        const result = readLookup(body);
        setState({ status: result ? 'ready' : 'error', entry: result?.blocked ? result.entry : null });
      },
      error => { if (!isAbortError(error)) setState({ status: 'error', entry: null }); },
    );
    return () => controller.abort();
  }, [client, e164, access.read, tick]);
  if (!access.read) return null;
  const entry = state.entry;
  return (
    <div className="bl-phone-status" aria-label={label}>
      <BlocklistInlineStyles />
      {entry ? (
        <span className="chip tone-end" title={entry.reason}>
          <Icon icon={Ban} /> {blockedChipText(entry)}
          <span className="visually-hidden">. Reason: {clipReason(entry.reason, 120)}</span>
        </span>
      ) : null}
      {state.status === 'error' ? <span className="meta">Couldn’t check the blocked list.</span> : null}
      {showControl && access.manage && state.status !== 'loading' ? (
        <BlockNumberButton e164={e164} client={client} noteSeed={noteSeed} blocked={!!entry} onSaved={() => setTick(t => t + 1)} className="btn btn-small btn-quiet" />
      ) : null}
    </div>
  );
}
